import React from 'react';
import {
  getSoundtrack, removeFromSoundtrack, onSoundtrackChange,
} from '../lib/soundtrack';

// Msg #800 enhancement — Small persistent player mounted on ChatPage.
// Watches:
//   • the user's saved soundtrack (localStorage)
//   • presence in the current room (users list + NPC locations)
// and auto-plays the music of the FIRST saved NPC that is currently in the
// same room as the user. Falls silent when none of the user's saved NPCs are
// nearby. Click ⤒ to expand / ⤓ to collapse; ✕ to remove the current track.
//
// The music_html is reused verbatim from the character profile (already goes
// through the same `_prepareMusicHtml` transform on save, so autoplay params
// are already baked in when the iframe mounts here).
export default function AdventureSoundtrackPlayer({
  room,
  presenceNames = [],
  npcLocations = {},
  apiBase,
}) {
  void apiBase;
  const [playlist, setPlaylist] = React.useState(getSoundtrack);
  const [expanded, setExpanded] = React.useState(false);

  React.useEffect(() => onSoundtrackChange(setPlaylist), []);

  // Which saved NPC (if any) is currently in this room?
  const currentTrack = React.useMemo(() => {
    if (!room || playlist.length === 0) return null;
    // Build a lowercase set of everyone in the room (users + NPCs).
    const here = new Set();
    presenceNames.forEach((n) => here.add(String(n).toLowerCase()));
    Object.entries(npcLocations || {}).forEach(([npc, where]) => {
      if (where === room) here.add(String(npc).toLowerCase());
    });
    return playlist.find((t) => here.has(String(t.npc).toLowerCase())) || null;
  }, [room, playlist, presenceNames, npcLocations]);

  // Nothing saved AND nothing playing? Render nothing (keep the UI tidy).
  if (playlist.length === 0) return null;

  return (
    <div
      data-testid="soundtrack-player"
      style={{
        position: 'fixed',
        bottom: 12,
        right: 12,
        width: expanded ? 320 : 210,
        background: 'linear-gradient(180deg, #1a2a1a 0%, #0f1a0f 100%)',
        border: '1px solid #5a4a1d',
        boxShadow: '0 4px 12px rgba(0,0,0,0.55)',
        color: '#f5e6c0',
        fontFamily: 'Georgia, serif',
        fontSize: 12,
        zIndex: 9800,
        overflow: 'hidden',
      }}
    >
      {/* HEADER */}
      <div
        style={{
          display: 'flex', alignItems: 'center', gap: 6,
          padding: '4px 8px',
          background: 'rgba(0,0,0,0.35)',
          borderBottom: '1px solid #5a4a1d',
          fontSize: 11, letterSpacing: 1, textTransform: 'uppercase',
        }}
      >
        <span data-testid="soundtrack-header" style={{ flex: 1 }}>
          ♬ {currentTrack
            ? currentTrack.npc.replace(/_/g, ' ')
            : `Soundtrack (${playlist.length})`}
        </span>
        <button
          data-testid="soundtrack-toggle"
          onClick={() => setExpanded((v) => !v)}
          title={expanded ? 'Collapse' : 'Show playlist'}
          style={{
            background: 'transparent', color: '#c8a94d',
            border: '1px solid #5a4a1d', cursor: 'pointer',
            padding: '0 6px', fontSize: 12,
          }}
        >
          {expanded ? '⤓' : '⤒'}
        </button>
      </div>

      {/* NOW PLAYING */}
      {currentTrack ? (
        <div
          key={currentTrack.npc}   /* re-mount iframe when track switches */
          data-testid={`soundtrack-track-${currentTrack.npc}`}
          style={{ padding: 4 }}
          dangerouslySetInnerHTML={{ __html: currentTrack.music_html }}
        />
      ) : (
        <div
          data-testid="soundtrack-idle"
          style={{
            padding: '10px 12px',
            fontStyle: 'italic',
            color: '#c8a94d',
            fontSize: 11,
          }}
        >
          None of your saved NPCs are in {room ? room.replace(/_/g, ' ') : 'this room'}.
        </div>
      )}

      {/* PLAYLIST DRAWER */}
      {expanded && (
        <div
          data-testid="soundtrack-drawer"
          style={{
            borderTop: '1px solid #5a4a1d',
            maxHeight: 180,
            overflowY: 'auto',
            background: '#0e160e',
          }}
        >
          {playlist.map((t) => (
            <div
              key={t.npc}
              data-testid={`soundtrack-item-${t.npc}`}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '4px 8px',
                borderBottom: '1px dashed #2a3a2a',
                background: currentTrack && currentTrack.npc === t.npc
                  ? 'rgba(200,169,77,0.10)' : 'transparent',
              }}
            >
              <span style={{ flex: 1 }}>{t.npc.replace(/_/g, ' ')}</span>
              <button
                data-testid={`soundtrack-remove-${t.npc}`}
                onClick={() => removeFromSoundtrack(t.npc)}
                title="Remove from playlist"
                style={{
                  background: 'transparent', color: '#c8a94d',
                  border: '1px solid #5a4a1d', cursor: 'pointer',
                  padding: '0 6px', fontSize: 11,
                }}
              >✕</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
